import React from "react";
import { useTranslation } from "react-i18next";
import { Search, Filter } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type {
  NeedsActionSortBy,
  NeedsActionSortOrder,
} from "../../domain/entities/NeedsAction";
import { textStyles } from "@/lib/textStyles";

interface SortOption {
  label: string;
  sortBy: NeedsActionSortBy;
  sortOrder: NeedsActionSortOrder;
}

interface NeedsActionHeaderProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  sortBy: NeedsActionSortBy;
  sortOrder: NeedsActionSortOrder;
  onSortChange: (
    sortBy: NeedsActionSortBy,
    sortOrder: NeedsActionSortOrder,
  ) => void;
  totalItems: number;
}

export const NeedsActionHeader: React.FC<NeedsActionHeaderProps> = ({
  searchQuery,
  onSearchChange,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const { t } = useTranslation("needsAction");

  const sortOptions: SortOption[] = [
    { label: t("sort.newestFirst"), sortBy: "status_at", sortOrder: "desc" },
    { label: t("sort.oldestFirst"), sortBy: "status_at", sortOrder: "asc" },
  ];

  const activeSort = sortOptions.find(
    (o) => o.sortBy === sortBy && o.sortOrder === sortOrder,
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-gray-800" style={textStyles.heading2}>
            {t("pageTitle")}
          </h1>
          {/* {totalItems > 0 && (
            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-destructive-text">
              {totalItems} pending
            </span>
          )} */}
          {/* <AlertCircle className="w-5 h-5 text-amber-500" /> */}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="flex w-full flex-col gap-3 sm:flex-1 sm:flex-row sm:items-center">
          <div className="relative w-full sm:max-w-[300px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 " />
            <Input
              placeholder={t("search.placeholder")}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-10 pl-10 bg-white border-gray-200"
              name="search"
              autoComplete="off"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="h-10 w-full cursor-pointer sm:w-auto"
              >
                <Filter className="mr-2 w-4 h-4" />
                {activeSort?.label ?? t("sort.label")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="w-40">
              <div className="px-2 py-1.5 text-xs font-semibold text-gray-500">
                {t("sort.sortBy")}
              </div>
              {sortOptions.map((option) => (
                <DropdownMenuItem
                  key={`${option.sortBy}-${option.sortOrder}`}
                  onClick={() => onSortChange(option.sortBy, option.sortOrder)}
                  className={`cursor-pointer ${
                    option === activeSort ? "text-primary" : "text-gray-700"
                  }`}
                >
                  {option.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
